import React from "react";
import { FieldErrors } from "react-hook-form";
import { UseFormRegister } from "react-hook-form/dist/types/form";
import { CampaignTypes } from "../../../models/Campaign";
import dayjs from "dayjs";
import { twJoin } from "tailwind-merge";
import { useTranslation } from "react-i18next";
type Props = {
  campaign: CampaignTypes;
  setCampaign: React.Dispatch<React.SetStateAction<CampaignTypes>>;
  register: UseFormRegister<CampaignTypes>;
  errors?: FieldErrors<CampaignTypes>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  isValid: boolean;
};

const StepOne = ({
  campaign,
  setCampaign,
  register,
  errors,
  setStep,
  isValid,
}: Props) => {
  const continueButtonDisabled =
    !campaign.startDate || !campaign.title || !campaign.category;
  const { t } = useTranslation();
  return (
    <div className="flex flex-col">
      <div className="bg-gray-100 px-4 pb-4 rounded-b-md">
        <div className="pt-5">
          <label
            htmlFor="campaignCateogry"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepOne.offerCategory")}{" "}
            <span className="text-orange-600">*</span>
          </label>

          <select
            className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full"
            {...register("category", {
              onChange: (e) => {
                setCampaign({
                  ...campaign,
                  category: e.target.value,
                });
              },
              required: {
                value: true,
                message: "Category is required",
              },
            })}
          >
            <option value="">{t("stepOne.selectCategory")}</option>
            <option value="Promotions">{t("stepOne.promotions")}</option>
            <option value="Service">{t("stepOne.service")}</option>
            <option value="Info">{t("stepOne.info")}</option>
          </select>
          {errors?.category && (
            <span className="text-red-500 text-sm">
              {errors.category.message}
            </span>
          )}
        </div>
        <div className="mt-5">
          <label
            htmlFor="campaignName"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepOne.permanentCampaign")}{" "}
            <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1 flex items-center">
            <button
              type="button"
              disabled={campaign.isPermanent}
              className={`w-1/5 py-2 focus:outline-none rounded-md me-2 border-2 border-indigo-300 ${
                campaign.isPermanent
                  ? "bg-indigo-200 hover:bg-indigo-300 text-indigo-600"
                  : "bg-gray-50 text-gray-500"
              }`}
              onClick={() => {
                setCampaign({
                  ...campaign,
                  isPermanent: true,
                });
              }}
            >
              {t("common.yes")}
            </button>
            <button
              type="button"
              disabled={!campaign.isPermanent}
              className={`w-1/5 py-2 focus:outline-none rounded-md border-2 border-indigo-300  ${
                !campaign.isPermanent
                  ? "bg-indigo-200 hover:bg-indigo-300 text-indigo-600"
                  : "bg-gray-50 text-gray-500"
              }`}
              onClick={() => {
                setCampaign({
                  ...campaign,
                  isPermanent: false,
                });
              }}
            >
              {t("common.no")}
            </button>
          </div>
        </div>

        <div className="mt-5">
          <label
            htmlFor="campaignTitle"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepOne.campaignTitle")}{" "}
            <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <input
              type="text"
              id="campaignTitle"
              className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full"
              {...register("title", {
                onChange: (e: any) => {
                  setCampaign({
                    ...campaign,
                    title: e.target.value,
                  });
                },
                required: {
                  value: true,
                  message: t("stepOne.titleRequired"),
                },
              })}
            />
          </div>
          <span className="text-red-500 text-xs">{errors?.title?.message}</span>
        </div>

        <div className="mt-5 flex">
          <div className="w-1/2 me-2">
            <label
              htmlFor="campaignDate"
              className="block text-sm font-bold text-gray-700"
            >
              {t("stepOne.startDate")}{" "}
              <span className="text-orange-600">*</span>
            </label>
            <div className="mt-1">
              <input
                type="date"
                id="campaignDate"
                className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none me-2 w-full"
                {...register("startDate", {
                  onChange: (e: any) => {
                    setCampaign({
                      ...campaign,
                      startDate: e.target.value,
                    });
                  },
                  required: {
                    value: true,
                    message: t("stepOne.startDateRequired"),
                  },
                })}
                value={
                  campaign.startDate
                    ? dayjs(new Date(campaign.startDate)).format("YYYY-MM-DD")
                    : undefined
                }
              />
            </div>
            <span className="text-red-500 text-xs">
              {errors?.startDate?.message}
            </span>
          </div>

          {!campaign.isPermanent && (
            <div className="w-1/2">
              <label
                htmlFor="campaignDate"
                className="block text-sm font-bold text-gray-700"
              >
                {t("stepOne.endDate")}{" "}
                <span className="text-orange-600">*</span>
              </label>
              <div className="mt-1">
                <input
                  type="date"
                  id="campaignDate"
                  className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none me-2 w-full"
                  {...register("endDate", {
                    onChange: (e: any) => {
                      setCampaign({
                        ...campaign,
                        endDate: e.target.value,
                      });
                    },
                    required: {
                      value: !campaign.isPermanent,
                      message: t("stepOne.endDateRequired"),
                    },
                  })}
                />
              </div>
              <span className="text-red-500 text-xs">
                {errors?.endDate?.message}
              </span>
            </div>
          )}
        </div>
      </div>
      <button
        disabled={continueButtonDisabled}
        type="button"
        className={twJoin(
          "w-64 bg-indigo-600 disabled:bg-indigo-400 text-white py-2 rounded-md focus:outline-none mt-7 self-end",
          continueButtonDisabled && "pointer-events-none"
        )}
        onClick={() => setStep(2)}
      >
        {t("common.continue")}
      </button>
    </div>
  );
};

export default StepOne;
